import type { SubmitEvent as ReactSubmitEvent } from 'react';
import React, { useEffect, useState } from 'react';
import Filter from '@sats-group/icons/16/filter';
import LinkExternal from '@sats-group/icons/16/link-external';
import Link from '@sats-group/fresh-fitness-ui-lib/react/link';
import Logout from '@sats-group/icons/16/logout';
import OpenFolder from '@sats-group/icons/16/open-folder';
import Outdoors from '@sats-group/icons/16/outdoors';
import Person from '@sats-group/icons/16/person';
import Refresh from '@sats-group/icons/16/refresh';
import Save from '@sats-group/icons/16/save';
import Share from '@sats-group/icons/16/share';
import StarStickerCheck from '@sats-group/icons/16/star-sticker-check';
import Check24 from '@sats-group/icons/24/check';
import Close24 from '@sats-group/icons/24/close';
import Button from '@sats-group/fresh-fitness-ui-lib/react/button';
import LinkButton from '@sats-group/fresh-fitness-ui-lib/react/link-button';
import Message from '@sats-group/fresh-fitness-ui-lib/react/message';
import Text from '@sats-group/fresh-fitness-ui-lib/react/text';

import Logo from 'root/client/components/logo/logo';

import Modal from '../../components/modal/modal';
import { gitHash } from '../../git-hash.generated';

import type { WebAdminPage as Props } from './web-admin-page.types';
import SitemapNode from './sitemap-node';

const wait = (seconds: number) =>
  new Promise(resolve => setTimeout(resolve, seconds * 1000));

const adminSections = [
  { id: 'refresh', title: 'Refresh', icon: Refresh },
  { id: 'refresh-self-service', title: 'Refresh Self Service', icon: Person },
  { id: 'campaigns', title: 'Campaigns', icon: StarStickerCheck },
  { id: 'git-log', title: 'GitLog', icon: Share },
  { id: 'sitemap', title: 'Sitemap', icon: Outdoors },
  { id: 'config', title: 'Config', icon: Filter },
  { id: 'data', title: 'Data', icon: Save },
  {
    id: 'tracked-contentful-types',
    title: 'Tracked Contentful Types',
    icon: OpenFolder,
  },
] as const;

type AdminSection = (typeof adminSections)[number];

type RefreshState = {
  isRefreshing: boolean;
  message?: string;
};

const initialRefreshState: RefreshState = { isRefreshing: false };

const WebAdminPage: React.FunctionComponent<Props> = ({
  campaigns,
  contentful,
  data = {},
  dateTimeIso,
  environmentVariables = {},
  gitLog,
  logOut,
  messages = [],
  sitemap,
  title,
}) => {
  const [webRefresh, setWebRefresh] =
    useState<RefreshState>(initialRefreshState);
  const [selfServiceRefresh, setSelfServiceRefresh] =
    useState<RefreshState>(initialRefreshState);

  const [copyState, setCopyState] = useState<string>();
  const [modalData, setModalData] = useState<string>();
  const [dataBuildLocalTime, setDataBuildLocalTime] =
    useState('calculating...');
  const [activeSection, setActiveSection] = useState<AdminSection>(
    adminSections[0],
  );

  useEffect(() => {
    setDataBuildLocalTime(new Date(dateTimeIso).toLocaleString());
  }, [dateTimeIso]);

  const checkDataJobStatus = async (
    url: string,
    setRefreshState: React.Dispatch<React.SetStateAction<RefreshState>>,
    currentTry = 0,
    maxTries = 30,
  ): Promise<void> => {
    if (currentTry >= maxTries) {
      setRefreshState({
        isRefreshing: false,
        message: `❌Maximum number of status checks (${maxTries}) reached on ${url}`,
      });
      return;
    }

    await wait(currentTry * 0.5);

    setRefreshState({
      isRefreshing: true,
      message: `⏳Checking status (${currentTry}/${maxTries})`,
    });

    const result = await fetch(url);

    if (result.status === 202) {
      return checkDataJobStatus(url, setRefreshState, currentTry + 1, maxTries);
    }

    if (result.ok) {
      setRefreshState({
        isRefreshing: false,
        message: `✅Success! Data refreshed in (${currentTry}/${maxTries}) tries`,
      });
    } else {
      setRefreshState({
        isRefreshing: false,
        message: `❌Status check failed (${currentTry}/${maxTries}) with status ${result.status} ${result.statusText}`,
      });
    }
  };

  const handleDataJobSubmission = async (
    event: ReactSubmitEvent<HTMLFormElement>,
    setRefreshState: React.Dispatch<React.SetStateAction<RefreshState>>,
  ) => {
    event.preventDefault();

    setRefreshState({ isRefreshing: true, message: `⏳Refreshing data` });

    const { action } = event.currentTarget;
    const { submitter } = event;

    if (!(submitter instanceof HTMLButtonElement)) {
      setRefreshState({
        isRefreshing: false,
        message: '❌Something went wrong while refreshing data',
      });
      return;
    }

    const { name, value } = submitter;

    const body = new FormData();
    body.set(name, value);

    try {
      const result = await fetch(action, {
        method: 'post',
        body,
      });

      const location = result.headers.get('location');
      if (!location) {
        throw new Error('Missing location header');
      }

      await checkDataJobStatus(location, setRefreshState);
    } catch {
      setRefreshState({
        isRefreshing: false,
        message: '❌Something went wrong while refreshing data',
      });
    }
  };

  const hideData = () => setModalData(undefined);
  const showData = (data: string) => () => setModalData(data);
  const copyData = (data: unknown) => () =>
    navigator.clipboard
      .writeText(String(data))
      .then(() => setCopyState('✅'))
      .catch(() => setCopyState('❌'))
      .finally(() => setTimeout(() => setCopyState(undefined), 5000));

  return (
    <div className="web-admin-page">
      <Modal
        closeButtonText="Close"
        hide={hideData}
        isVisible={Boolean(modalData)}
        title="Data"
      >
        <div style={{ alignItems: 'center', display: 'flex', margin: '1rem' }}>
          <Button
            onClick={copyData(modalData)}
            size={Button.sizes.small}
            text="📋 Copy"
          />
          <span style={{ marginLeft: '1rem' }}>{copyState}</span>
        </div>
        <div style={{ margin: '1rem' }}>
          <code>
            <pre>{modalData}</pre>
          </code>
        </div>
      </Modal>
      {messages.length === 0 ? null : (
        <ul className="web-admin-page__messages">
          {messages.map(message => (
            <li key={message.text}>
              <Message {...message} />
            </li>
          ))}
        </ul>
      )}
      <div className="web-admin-page__workspace">
        <div className="web-admin-page__nav-wrapper">
          <div className="web-admin-page__logo">
            <Logo label="home" theme={Logo.themes.DarkRed} />
          </div>
          <div className="web-admin-page__logout">
            <LinkButton
              href={logOut.href}
              icon={<Logout />}
              text={logOut.label}
              theme={LinkButton.themes.tertiary}
            />
          </div>
          <nav
            aria-label="Web admin sections"
            className="web-admin-page__navigation"
          >
            <ul className="web-admin-page__navigation-list">
              {adminSections.map(section => {
                const Icon = section.icon;

                return (
                  <li key={section.id}>
                    <button
                      aria-current={
                        activeSection.id === section.id ? 'page' : undefined
                      }
                      className="web-admin-page__navigation-button"
                      onClick={() => setActiveSection(section)}
                      type="button"
                    >
                      <span
                        aria-hidden="true"
                        className="web-admin-page__navigation-icon"
                      >
                        <Icon />
                      </span>
                      <span>{section.title}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
        <section
          aria-labelledby="web-admin-active-section"
          className="web-admin-page__content"
        >
          <div className="web-admin-page__header">
            <Text
              elementName="h1"
              size={Text.sizes.header2}
              variant={Text.variants.hero}
            >
              {title}
            </Text>
            <div className="web-admin-page__metadata">
              <div className="web-admin-page__metadata-item">
                <Text theme={Text.themes.emphasis}>Data last generated</Text>
                <Text>
                  <time dateTime={dateTimeIso}>{dataBuildLocalTime}</time>
                </Text>
              </div>
              <div className="web-admin-page__metadata-item">
                <Text theme={Text.themes.emphasis}>Application version</Text>
                <Text>
                  <code>{gitHash}</code>
                </Text>
              </div>
            </div>
          </div>
          <Text
            elementName="h2"
            id="web-admin-active-section"
            size={Text.sizes.header3}
            variant={Text.variants.hero}
          >
            {activeSection.title}
          </Text>
          <div className="web-admin-page__section-body">
            {activeSection.id === 'refresh' ? (
              <div className="web-admin-page__refresh-data">
                <Text theme={Text.themes.emphasis}>Job Status</Text>
                {webRefresh.message ? (
                  <Text>{webRefresh.message}</Text>
                ) : (
                  <Text>No jobs currently running</Text>
                )}
                <Text theme={Text.themes.emphasis}>Choose data to refresh</Text>
                <form
                  action="/api/admin-start-data-job"
                  encType="application/x-www-form-urlencoded"
                  onSubmit={event => {
                    void handleDataJobSubmission(event, setWebRefresh);
                  }}
                  method="post"
                >
                  <div className="web-admin-page__refresh-job-section">
                    <Button
                      type="submit"
                      name="job"
                      value="refresh-all"
                      disabled={webRefresh.isRefreshing}
                      loading={webRefresh.isRefreshing}
                      text="All data"
                      theme={Button.themes.secondary}
                      wide
                    />
                    <Button
                      type="submit"
                      name="job"
                      value="refresh-contentful"
                      disabled={webRefresh.isRefreshing}
                      loading={webRefresh.isRefreshing}
                      text="Contentful"
                      theme={Button.themes.secondary}
                      wide
                    />
                    <Button
                      type="submit"
                      name="job"
                      value="refresh-platform"
                      disabled={webRefresh.isRefreshing}
                      loading={webRefresh.isRefreshing}
                      text="Platform"
                      theme={Button.themes.secondary}
                      wide
                    />
                  </div>
                </form>
              </div>
            ) : null}
            {activeSection.id === 'refresh-self-service' ? (
              <div className="web-admin-page__refresh-data">
                <Text theme={Text.themes.emphasis}>Job Status</Text>
                {selfServiceRefresh.message ? (
                  <Text>{selfServiceRefresh.message}</Text>
                ) : (
                  <Text>No jobs currently running</Text>
                )}
                <Text theme={Text.themes.emphasis}>Choose data to refresh</Text>
                <form
                  action="/api/admin-start-data-job"
                  encType="application/x-www-form-urlencoded"
                  onSubmit={event => {
                    void handleDataJobSubmission(event, setSelfServiceRefresh);
                  }}
                  method="post"
                >
                  <Button
                    type="submit"
                    name="job"
                    value="self-service-cache-contentful"
                    disabled={selfServiceRefresh.isRefreshing}
                    loading={selfServiceRefresh.isRefreshing}
                    text="Contentful"
                    theme={Button.themes.secondary}
                    wide
                  />
                </form>
              </div>
            ) : null}
            {activeSection.id === 'campaigns' ? (
              <table className="web-admin-page__table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Start date</th>
                    <th>End date</th>
                    <th>Default</th>
                    <th>Valid</th>
                    <th>Club ID</th>
                    <th>Payment type ID</th>
                    <th>Contentful Entry</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map(campaign => (
                    <tr
                      key={`${campaign.code}-${campaign.clubId}-${campaign.paymentTypeId}-${campaign.startDate}`}
                    >
                      <td>{campaign.code}</td>
                      <td>{campaign.startDate}</td>
                      <td>{campaign.endDate}</td>
                      <td>{campaign.isDefault ? 'Yes' : 'No'}</td>
                      <td>
                        <span
                          aria-label={campaign.isValid ? 'Valid' : 'Invalid'}
                          className={`web-admin-page__campaign-validity web-admin-page__campaign-validity--${campaign.isValid ? 'valid' : 'invalid'}`}
                          role="img"
                        >
                          {campaign.isValid ? <Check24 /> : <Close24 />}
                        </span>
                      </td>
                      <td>{campaign.clubId ?? '—'}</td>
                      <td>{campaign.paymentTypeId ?? '—'}</td>
                      <td>
                        {campaign.editorialContentLink ? (
                          <Link
                            href={campaign.editorialContentLink}
                            label="Go to entry"
                            target="_blank"
                            trailingIcon={<LinkExternal />}
                          />
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            {activeSection.id === 'git-log' ? (
              <div className="web-admin-page__git-log">
                {gitLog &&
                  gitLog.map(
                    ({
                      date,
                      emoji,
                      hash,
                      message,
                      pr,
                      prUrl,
                      trackerId,
                      trackerUrl,
                    }) => (
                      <div className="web-admin-page__git-log-entry" key={hash}>
                        <div className="web-admin-page__git-log-emoji">
                          {emoji}
                        </div>
                        <div className="web-admin-page__git-log-text">
                          <div>
                            {trackerId && trackerUrl ? (
                              <React.Fragment>
                                <a
                                  href={trackerUrl}
                                  rel="noreferrer"
                                  target="_blank"
                                >
                                  {trackerId}
                                </a>{' '}
                              </React.Fragment>
                            ) : null}
                            {message}
                          </div>
                          <div>
                            {date}, <a href={prUrl}>{pr}</a>, {hash}
                          </div>
                        </div>
                      </div>
                    ),
                  )}
              </div>
            ) : null}
            {activeSection.id === 'sitemap' ? (
              <SitemapNode data={sitemap} />
            ) : null}
            {activeSection.id === 'config' ? (
              <table>
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(environmentVariables).map(([key, value]) => (
                    <tr key={key}>
                      <td>
                        <code>{key}</code>
                      </td>
                      <td>
                        <code>{value}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            {activeSection.id === 'data' ? (
              <table>
                <thead>
                  <tr>
                    <th>System</th>
                    <th>Key</th>
                    <th>Size</th>
                    <th>Hash</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data).map(([system, value]) => (
                    <React.Fragment key={system}>
                      {Object.entries(value).map(
                        ([file, { bytes, data, hash, humanReadableSize }]) => (
                          <tr key={file}>
                            <td>{system}</td>
                            <td>
                              <button
                                onClick={showData(String(data))}
                                style={{
                                  background: 'transparent',
                                  border: 0,
                                  fontSize: '1rem',
                                  margin: '0 .5rem 0 0',
                                  padding: 0,
                                }}
                              >
                                🔍
                              </button>
                              <code>{file}</code>
                            </td>
                            <td>
                              <code data-bytes={bytes}>
                                {humanReadableSize}
                              </code>
                            </td>
                            <td>
                              <code>{hash}</code>
                            </td>
                          </tr>
                        ),
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            ) : null}
            {activeSection.id === 'tracked-contentful-types' ? (
              <table className="web-admin-page__table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Type</th>
                    <th>Fields</th>
                  </tr>
                </thead>
                <tbody>
                  {contentful.map(([contentTypeId, type, fields]) => (
                    <tr key={contentTypeId}>
                      <th>{contentTypeId}</th>
                      <td>{type}</td>
                      <td>{fields.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
};

export default WebAdminPage;
